import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../api/client";
import ChatMarkdown, { linkifyPlain, messageText } from "../components/ChatMarkdown";

interface ChatMessage {
  ts: string;
  role: "user" | "assistant";
  content?: unknown;
  text?: string;
  error?: boolean;
}

export default function Chat() {
  const qc = useQueryClient();
  const q = useQuery<{ messages: ChatMessage[] }>({
    queryKey: ["chat-history"],
    queryFn: () => apiGet("/api/chat/history"),
  });
  const send = useMutation({
    mutationFn: (message: string) => apiPost<{ reply: string }>("/api/chat/messages", { message }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-history"] }),
  });
  const clear = useMutation({
    mutationFn: () => apiDelete<{ ok: boolean; deleted: number }>("/api/chat/history"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-history"] }),
  });

  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [q.data?.messages, send.isPending]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const text = input.trim();
    if (!text || send.isPending) return;
    setInput("");
    send.mutate(text);
  }

  const messages = q.data?.messages ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 120px)" }}>
      <div className="row" style={{ marginBottom: 8 }}>
        <h1 style={{ margin: 0 }}>Chat</h1>
        <span className="muted" style={{ fontSize: 12 }}>
          Ask questions about your tracked listings
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={() => clear.mutate()}
          disabled={clear.isPending || messages.length === 0}
          style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text)" }}
        >
          Clear conversation
        </button>
      </div>

      <div
        ref={scrollRef}
        className="panel"
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {q.isLoading && <p className="muted">Loading history…</p>}
        {messages.length === 0 && !q.isLoading && <Suggestions onPick={(t) => send.mutate(t)} />}
        {messages.map((m, i) => (
          <Bubble key={`${m.ts}-${i}`} message={m} />
        ))}
        {send.isPending && (
          <div className="muted" style={{ fontSize: 13 }}>
            Thinking…
          </div>
        )}
        {send.error && (
          <div className="tag-error" style={{ fontSize: 13 }}>
            Error: {String(send.error)}
          </div>
        )}
      </div>

      <form
        onSubmit={submit}
        style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "flex-end" }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit(e);
            }
          }}
          placeholder="Ask: 'which 4-bed actives have the best schools' or 'what's the median sold price in Holmdel last 60 days?'"
          rows={2}
          style={{ flex: 1, resize: "vertical", minHeight: 44, fontFamily: "inherit" }}
          disabled={send.isPending}
        />
        <button type="submit" disabled={!input.trim() || send.isPending}>
          Send
        </button>
      </form>
    </div>
  );
}

function Suggestions({ onPick }: { onPick: (text: string) => void }) {
  const ideas = [
    "Which active listings best match my criteria (4+ beds, 3000+ sqft, schools ≥ 7)?",
    "What's the median list-vs-sold delta in Holmdel over the last 60 days?",
    "Are any listings overpriced relative to comps?",
    "Show me recent sales in Middletown sorted by deepest price drop.",
  ];
  return (
    <div className="muted" style={{ fontSize: 13 }}>
      <p style={{ margin: "0 0 8px" }}>Try one of these:</p>
      <ul style={{ paddingLeft: 18, margin: 0 }}>
        {ideas.map((t) => (
          <li key={t} style={{ marginBottom: 4 }}>
            <button
              onClick={() => onPick(t)}
              style={{
                background: "transparent",
                border: 0,
                color: "var(--accent)",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
              }}
            >
              {t}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  const text = messageText(message);
  return (
    <div
      style={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "80%",
        background: isUser ? "var(--accent)" : "var(--bg)",
        color: isUser ? "white" : "var(--text)",
        padding: "10px 14px",
        borderRadius: 12,
        border: isUser ? "none" : "1px solid var(--border)",
        // The assistant side is markdown, which brings its own block spacing.
        whiteSpace: isUser ? "pre-wrap" : "normal",
        lineHeight: 1.45,
      }}
    >
      {isUser ? linkifyPlain(text) : <ChatMarkdown text={text} />}
    </div>
  );
}

